import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seo";
import { STUDIO_EMAIL, EMAIL_HREF } from "@/lib/links";

const ease = [0.22, 1, 0.36, 1];
const LAST_UPDATED = "20 February 2026";

const SECTIONS = [
    {
        no: "01",
        title: "Information we collect",
        body: (
            <>
                <p>
                    We collect only what we need to run this site and reply to
                    genuine enquiries. That's it. We don't sell data, we don't
                    build advertising profiles, and we don't share information
                    with people who shouldn't have it.
                </p>
                <ul className="mt-5 space-y-3 list-none">
                    <li>
                        <span className="text-olive">— </span>
                        Details you send through the contact form: name, email,
                        company (optional), stage, budget (optional) and your
                        message.
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        Anonymous usage data via Google Analytics 4 and Microsoft
                        Clarity — page views, device/browser, approximate
                        location, and interaction events like WhatsApp button
                        clicks. Only after you accept cookies.
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        Standard server logs (IP, timestamp) held by our hosting
                        provider for security and abuse prevention.
                    </li>
                </ul>
            </>
        ),
    },
    {
        no: "02",
        title: "Contact form submissions (Formspree)",
        body: (
            <>
                <p>
                    The contact form is handled by{" "}
                    <a
                        href="https://formspree.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-4 decoration-olive hover:text-olive transition-colors duration-200"
                    >
                        Formspree
                    </a>
                    , a third-party form service. When you submit, the details
                    you enter are transmitted to Formspree and forwarded to us
                    by email. Formspree also attaches four hidden fields for
                    tracking: the page you submitted from, the source
                    (digitalbeej.in), a timestamp, and a subject line.
                </p>
                <p className="mt-4">
                    We use these submissions solely to reply to your enquiry. We
                    do not add you to a marketing list, and we do not share your
                    details with third parties.
                </p>
            </>
        ),
    },
    {
        no: "03",
        title: "Google Analytics 4",
        body: (
            <>
                <p>
                    We use Google Analytics 4 (GA4) to understand which pages
                    people visit, which CTAs they click, and how they arrived —
                    so we can improve the site. Data is aggregated and
                    anonymised; we don't see individual users.
                </p>
                <p className="mt-4">
                    GA4 loads only after you click{" "}
                    <span className="text-olive">Accept</span> on the cookie
                    banner. If you decline (or later change your mind via
                    "Cookie preferences" in the footer), GA4 does not load at
                    all.
                </p>
            </>
        ),
    },
    {
        no: "04",
        title: "Microsoft Clarity",
        body: (
            <>
                <p>
                    We may use Microsoft Clarity to capture anonymised session
                    recordings and heatmaps — a bird's-eye view of how people
                    move through the site so we can spot friction and improve
                    it. Clarity masks form fields and sensitive input by
                    default; we cannot see what you type in the contact form
                    through Clarity.
                </p>
                <p className="mt-4">
                    Clarity also only loads after you click{" "}
                    <span className="text-olive">Accept</span>.
                </p>
            </>
        ),
    },
    {
        no: "05",
        title: "Cookies",
        body: (
            <>
                <p>
                    We use two kinds of storage:
                </p>
                <ul className="mt-5 space-y-3 list-none">
                    <li>
                        <span className="text-olive">— </span>
                        A single local-storage entry (
                        <span className="font-mono text-sm">db_consent</span>)
                        that records your consent choice so we don't ask again
                        on every page.
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        Analytics cookies from Google (GA4) and Microsoft
                        (Clarity) — set only after you accept.
                    </li>
                </ul>
                <p className="mt-5">
                    You can change your preference any time via{" "}
                    <span className="text-olive">Cookie preferences</span> in
                    the footer, or by clearing site data in your browser.
                </p>
            </>
        ),
    },
    {
        no: "06",
        title: "Data retention",
        body: (
            <>
                <p>
                    Contact-form submissions are retained in our email inbox for
                    as long as they remain useful for the engagement — typically
                    the lifetime of the project plus 24 months, after which they
                    are archived or deleted on request.
                </p>
                <p className="mt-4">
                    GA4 data is retained for 14 months by default (Google's
                    setting). Clarity data is retained per Microsoft's default
                    policy (approximately 13 months).
                </p>
            </>
        ),
    },
    {
        no: "07",
        title: "Third-party services",
        body: (
            <>
                <ul className="space-y-3 list-none">
                    <li>
                        <span className="text-olive">— </span>
                        <span className="font-bold">Formspree</span> — contact
                        form processing. Privacy:{" "}
                        <a
                            href="https://formspree.io/legal/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-4 decoration-olive hover:text-olive transition-colors duration-200"
                        >
                            formspree.io/legal/privacy-policy
                        </a>
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        <span className="font-bold">Google Analytics 4</span> —
                        aggregated analytics. Privacy:{" "}
                        <a
                            href="https://policies.google.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-4 decoration-olive hover:text-olive transition-colors duration-200"
                        >
                            policies.google.com/privacy
                        </a>
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        <span className="font-bold">Microsoft Clarity</span> —
                        anonymised session insights. Privacy:{" "}
                        <a
                            href="https://privacy.microsoft.com/privacystatement"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-4 decoration-olive hover:text-olive transition-colors duration-200"
                        >
                            privacy.microsoft.com
                        </a>
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        <span className="font-bold">WhatsApp</span> — for
                        conversations you initiate via the WhatsApp CTAs. Meta's
                        privacy policy applies once the chat opens.
                    </li>
                </ul>
            </>
        ),
    },
    {
        no: "08",
        title: "Your rights",
        body: (
            <>
                <p>
                    Under India's Digital Personal Data Protection Act (DPDP
                    2023), the EU GDPR and equivalent laws, you have the right
                    to:
                </p>
                <ul className="mt-5 space-y-3 list-none">
                    <li>
                        <span className="text-olive">— </span>
                        Ask what data we hold about you.
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        Ask us to correct or delete it.
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        Withdraw consent for analytics at any time via the
                        Cookie preferences link.
                    </li>
                    <li>
                        <span className="text-olive">— </span>
                        Ask us to stop replying if the conversation is
                        unwelcome.
                    </li>
                </ul>
                <p className="mt-5">
                    Email us at{" "}
                    <a
                        href={EMAIL_HREF}
                        className="underline underline-offset-4 decoration-olive hover:text-olive transition-colors duration-200"
                    >
                        {STUDIO_EMAIL}
                    </a>{" "}
                    with the subject line "Privacy request" and we'll respond
                    within 30 days.
                </p>
            </>
        ),
    },
    {
        no: "09",
        title: "Contact",
        body: (
            <>
                <p>
                    Questions about this policy or a specific piece of data?
                    Reach us at{" "}
                    <a
                        href={EMAIL_HREF}
                        className="underline underline-offset-4 decoration-olive hover:text-olive transition-colors duration-200"
                    >
                        {STUDIO_EMAIL}
                    </a>
                    . For anything urgent, WhatsApp works too — see the button
                    in the corner.
                </p>
            </>
        ),
    },
];

export default function Privacy() {
    return (
        <>
            <Seo {...SEO.privacy} />
            <PageHero
                data-testid="privacy-page-hero"
                kicker="/ Privacy · 06"
                meta={`Last updated · ${LAST_UPDATED}`}
                title="Privacy"
                accent="in plain English"
                tagline="What we collect, why we collect it, and exactly how to opt out."
            />

            <section
                data-testid="privacy-body"
                className="relative pb-24 lg:pb-32"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <div className="grid grid-cols-12 gap-y-16 lg:gap-x-12">
                        <div className="col-span-12 lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
                            <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                                On this page
                            </div>
                            <ol className="mt-6 space-y-3">
                                {SECTIONS.map((s) => (
                                    <li
                                        key={s.no}
                                        className="flex items-start gap-4 text-[15px]"
                                    >
                                        <span className="font-display font-black text-olive w-6 shrink-0">
                                            {s.no}
                                        </span>
                                        <a
                                            href={`#s-${s.no}`}
                                            className="text-black/70 hover:text-olive transition-colors duration-200 leading-snug"
                                        >
                                            {s.title}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <div className="col-span-12 lg:col-span-8">
                            {SECTIONS.map((s, i) => (
                                <motion.article
                                    id={`s-${s.no}`}
                                    key={s.no}
                                    data-testid={`privacy-section-${s.no}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{
                                        duration: 0.7,
                                        ease,
                                        delay: (i % 3) * 0.05,
                                    }}
                                    className="pt-12 lg:pt-16 pb-10 border-b border-black/15 first:pt-0"
                                >
                                    <div className="flex items-baseline gap-4">
                                        <span className="font-display font-black text-3xl lg:text-4xl text-olive">
                                            {s.no}
                                        </span>
                                        <h2 className="font-display font-black uppercase tracking-tight text-2xl lg:text-3xl leading-tight">
                                            {s.title}
                                        </h2>
                                    </div>
                                    <div className="mt-6 text-black/75 leading-relaxed max-w-2xl text-[17px]">
                                        {s.body}
                                    </div>
                                </motion.article>
                            ))}

                            <div className="mt-12 text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                                Last updated · {LAST_UPDATED}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
